import Keycloak from "keycloak-js";

/**
 * Sign-in via Keycloak's browser flow (authorization code + PKCE) on realm `centrics`, like
 * telebutler.app. The tokens live in this JS context only — nothing is written to storage. A
 * reload gets new ones through the Keycloak SSO cookie (one silent redirect), and the access
 * token is refreshed here before it runs out. The backend only verifies the access token.
 */
export const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL || "https://sso.velcoda.com",
  realm: import.meta.env.VITE_KEYCLOAK_REALM || "centrics",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "centrics-web",
});

// Seconds an access token must still be valid when it is handed to a request.
const MIN_VALIDITY_SECONDS = 30;

// `basic` is an optional client scope on realm `centrics`; without it the token has no `sub`,
// which the backend needs to find the person. The adapter adds `openid` itself.
const SCOPE = "openid basic";

let initPromise: Promise<boolean> | null = null;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Starts the session. Outside the public pages this sends the browser to the Keycloak login (or
 * straight back, when the SSO session is still alive) — the app is mounted only once tokens are
 * there. Public pages (activation, reset links, registration) never redirect.
 */
export function initKeycloak(isPublicPath: boolean): Promise<boolean> {
  if (!initPromise) {
    initPromise = keycloak
      .init({
        onLoad: isPublicPath ? undefined : "login-required",
        pkceMethod: "S256",
        checkLoginIframe: false,
        scope: SCOPE,
      })
      .then((authenticated) => {
        keycloak.onTokenExpired = () => {
          refreshToken(MIN_VALIDITY_SECONDS).catch(() => undefined);
        };
        return authenticated;
      })
      .catch((error) => {
        console.error("[keycloak] init failed", error);
        return false;
      });
  }
  return initPromise;
}

export function isAuthenticated(): boolean {
  return !!keycloak.authenticated;
}

/**
 * An access token valid for at least another 30 seconds, refreshed with Keycloak when needed;
 * null when the session is gone. Concurrent callers share one refresh.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!keycloak.authenticated) {
    return null;
  }
  try {
    await refreshToken(MIN_VALIDITY_SECONDS);
  } catch {
    return null;
  }
  return keycloak.token ?? null;
}

/** Forces a new access token, e.g. after the backend rejected the current one. */
export async function forceRefresh(): Promise<string | null> {
  if (!keycloak.authenticated) {
    return null;
  }
  try {
    await refreshToken(-1);
  } catch {
    return null;
  }
  return keycloak.token ?? null;
}

/** Keycloak's login page; back to `path` (default: the current page) afterwards. */
export function login(options: { path?: string; email?: string } = {}): Promise<void> {
  return keycloak.login({
    redirectUri: absolute(options.path ?? currentPath()),
    loginHint: options.email,
    scope: SCOPE,
  });
}

/**
 * Ends the Keycloak session too and lands on `path` (default: the app root, which leads to the
 * login page again). Local state is the caller's to clear before.
 */
export function logout(path = "/"): Promise<void> {
  if (!keycloak.authenticated) {
    window.location.assign(absolute(path));
    return Promise.resolve();
  }
  return keycloak.logout({ redirectUri: absolute(path) });
}

///////////// private helpers /////////////

function refreshToken(minValidity: number): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = keycloak.updateToken(minValidity).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function currentPath(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

function absolute(path: string): string {
  return new URL(path, window.location.origin).toString();
}
